// External libraries
import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';

// Internal modules
import { logger } from '@/lib/logger';
import { PP_WORLD_WALLET_OPTIONS_QUERY } from '@/lib/graphql';
import type {
  CountryCodes,
  PpWorldWalletOptionsQueryQuery,
  PpWorldWalletOptionsQueryQueryVariables,
} from '@/generated/graphql';
import { formatConjunctionList } from './formatConjunctionList';
import { normalizeCountryCode, normalizeLocale } from './normalizeLocale';
import { isVenmoOption } from '@/components/Billing/WalletsList/utils';
import type { CheckoutOption } from '@/components/Billing/WalletsList/WalletsList';
import {
  logBillingWalletOptionsFetchFailed,
  logBillingWalletOptionsShown,
} from '@/components/Billing/fpti';

// Raw checkout option shape as returned by xobuyernodeserv; derived from the
// generated schema types so this stays in sync with whatever xobuyernodeserv
// actually provides.
type GqlCheckoutOption =
  PpWorldWalletOptionsQueryQuery['ppWorldWalletOptions']['checkoutOptions'][number];

// Flattens the raw logoUrl/redirectUrl objects into plain strings for UI
// consumption.
const mapCheckoutOption = (option: GqlCheckoutOption): CheckoutOption => ({
  id: option.id,
  label: option.label,
  logoUrl: option.logoUrl.href || '',
  name: option.name,
  redirectUrl: option.redirectUrl.href || '',
});

type UiContent = {
  connectedByLabel: string;
  ppwWalletsLabel: string;
  ppwWalletsDescription: string;
};

type UseWalletOptionsParams = {
  token: string;
  locale: string;
  buyerCountry: CountryCodes;
};

type UseWalletOptionsResult = {
  isLoading: boolean;
  checkoutOptions: CheckoutOption[] | null;
  uiContent?: UiContent;
  ppwLogoUrl?: string;
};

// Shapes the raw query response into what the UI needs, or null checkout
// options if the buyer isn't eligible or no wallets are available.
const buildWalletOptionsResult = (
  walletOptions:
    | PpWorldWalletOptionsQueryQuery['ppWorldWalletOptions']
    | undefined,
  normalizedLocale: string,
): Omit<UseWalletOptionsResult, 'isLoading'> => {
  const gqlCheckoutOptions = walletOptions?.checkoutOptions;

  if (!walletOptions?.isEligible || !gqlCheckoutOptions?.length) {
    return { checkoutOptions: null };
  }

  const mappedOptions = gqlCheckoutOptions.map(mapCheckoutOption);

  // Filter out Venmo from wallet names for the description (only show PayPal World wallets)
  const ppwWalletNames = mappedOptions
    .filter(option => !isVenmoOption(option))
    .map(option => option.name);

  return {
    checkoutOptions: mappedOptions,
    ppwLogoUrl: walletOptions.payPalWorldLogoUrl.href || undefined,
    uiContent: {
      connectedByLabel: walletOptions.connectedByLabel,
      ppwWalletsDescription: formatConjunctionList(
        ppwWalletNames,
        normalizedLocale,
      ),
      ppwWalletsLabel: walletOptions.payPalWorldWalletsLabel,
    },
  };
};

export const useWalletOptions = ({
  token,
  locale,
  buyerCountry,
}: UseWalletOptionsParams): UseWalletOptionsResult => {
  const [fetchWalletOptions, { data, loading, called, error }] = useLazyQuery<
    PpWorldWalletOptionsQueryQuery,
    PpWorldWalletOptionsQueryQueryVariables
  >(PP_WORLD_WALLET_OPTIONS_QUERY, { fetchPolicy: 'no-cache' });

  const normalizedBuyerCountry = normalizeCountryCode(buyerCountry);
  const normalizedLocale = normalizeLocale(locale);

  useEffect(() => {
    const [localeLang, localeCountry] = normalizedLocale.split('_');

    fetchWalletOptions({
      variables: {
        buyerCountry: normalizedBuyerCountry,
        locale: { country: localeCountry, lang: localeLang },
        token,
      },
    });
  }, [normalizedBuyerCountry, normalizedLocale, fetchWalletOptions]);

  useEffect(() => {
    if (error) {
      logger.error('wallet_options_fetch_failed', { message: error.message });
      logBillingWalletOptionsFetchFailed(error.message);
    }
  }, [error]);

  const isLoading = loading || !called;
  const walletOptionsData = data?.ppWorldWalletOptions;
  const result = useMemo(
    () =>
      error
        ? { checkoutOptions: null }
        : buildWalletOptionsResult(walletOptionsData, normalizedLocale),
    [error, walletOptionsData, normalizedLocale],
  );

  useEffect(() => {
    if (result.checkoutOptions && result.checkoutOptions.length > 0) {
      logBillingWalletOptionsShown(result.checkoutOptions);
    }
  }, [result.checkoutOptions]);

  return {
    ...result,
    isLoading,
  };
};
